/**
 * Week · timeline.
 *
 * The week by line on one continuous time axis. A step (batch) is one card
 * from its start to its end, crossing midnight; inside it every crew line of
 * the labor standard shows its people as initials chips sized to the hours
 * they work, with open crew lines as "+" slots. Zoomed out, cards show only
 * their people; clicking a card focuses it — its hours stretch and the rest of
 * the week squeezes so it all stays on screen. The production day starts at
 * the first shift (7 am by default), nights are shaded, and the same chips
 * drag (or click, then click a crew line) exactly as on the other boards.
 * "Work plan" shows the same board with the slots empty.
 */
import { addDays, differenceInCalendarDays, format } from "date-fns";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDrop } from "react-dnd";

import { ShiftInstance } from "../../../../../services/shifts-api";
import { hhmm, toLocalIso } from "../../../utils/time";
import { ManualMoves } from "../controller/use-manual-moves";
import { bestPhaseTarget, DropState, phaseDropState } from "../utils/drop-state"; // eslint-disable-line sort-imports
import { LaneCard, LaneCrewGroup, LaneSlot } from "../utils/lane-rows"; // eslint-disable-line sort-imports
import { pinnedUntilLabel } from "../utils/pin-range";
import { Axis, buildAxis, isDayShift, packRows, personInitials, productCode, roleCode, Timeline, TimelineLane, TimelineStep } from "../utils/timeline"; // eslint-disable-line sort-imports
import { eyebrowSx, lbb } from "../utils/tokens";
import { MoveTarget } from "../utils/undo";
import { shiftLabelFor } from "../utils/week-lanes";
import { availabilityStripe, availabilityTitle, OUT_NAME_OPACITY, OutCount } from "./availability-badge"; // eslint-disable-line sort-imports
import { FillRequest } from "./fill-picker";
import { CARD_TYPE, CardDragItem, CardMenu, dropSurface, useCardDrag, useCardDrop, useCardHover, useLaneHeaderDrop, WorkerDragItem } from "./manual-controls"; // eslint-disable-line sort-imports
import { PeopleList, WORKER_LIST_WIDTH } from "./people-list";
import { KnownWorker } from "./standing-crew-panel";
import { PinEndOnDay } from "./week-line-view";

const LABEL_WIDTH = 128;
const WEEK_DAY_WIDTH = 340;
const HEADER_FULL = 28;
const HEADER_SLIM = 16;
const GROUP_LABEL = 16;
const SHIFT_LABEL = 14;
const ROW = 24;
const GROUP_PAD = 8;
/** Below this width a card shows short codes instead of names. */
const NARROW = 230;
const DOW = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const NIGHT = "rgba(26, 36, 66, 0.055)";

const crewColor = (have: number, needed: number): string =>
	needed === 0 ? lbb.ink60 : have === 0 ? lbb.brick : have < needed ? lbb.warning : lbb.success;

const hourLabel = (h: number): string => {
	const hour = ((Math.round(h) % 24) + 24) % 24;
	return `${hour % 12 || 12}${hour < 12 ? "am" : "pm"}`;
};

/** Plant-local ISO of a time `hours` after Monday 00:00 of the week. */
const isoAt = (weekStart: Date, hours: number): string => toLocalIso(new Date(weekStart.getTime() + hours * 3600000));

const clockOf = (hours: number): string => {
	const h = ((hours % 24) + 24) % 24;
	const whole = Math.floor(h);
	const minutes = Math.round((h - whole) * 60);
	return `${whole % 12 || 12}${minutes ? `:${String(minutes).padStart(2, "0")}` : ""} ${whole < 12 ? "am" : "pm"}`;
};

const readSetting = (key: string, fallback: string): string => {
	try {
		return window.localStorage.getItem(key) ?? fallback;
	} catch {
		return fallback;
	}
};
const writeSetting = (key: string, value: string): void => {
	try {
		window.localStorage.setItem(key, value);
	} catch {
		// The setting simply is not remembered.
	}
};

type Mode = "roster" | "plan";

interface GroupLayout {
	group: LaneCrewGroup;
	rows: number;
	height: number;
	cards: Array<{ card: LaneCard; row: number }>;
	slots: Array<{ slot: LaneSlot; row: number }>;
}

/** Each crew line's rows: people and open slots that overlap in time stack. */
function layoutGroups(step: TimelineStep, slim: boolean, mode: Mode): GroupLayout[] {
	return step.groups.map((group) => {
		const pieces = [
			...group.cards.map((card) => ({ start: card.start, end: card.end, card, slot: null as LaneSlot | null })),
			...group.slots.map((slot) => ({ start: slot.start, end: slot.end, card: null as LaneCard | null, slot })),
		];
		const { rows, placed } = packRows(pieces);
		const height = (slim ? 0 : GROUP_LABEL + SHIFT_LABEL) + rows * ROW + GROUP_PAD;
		return {
			group,
			rows,
			height: mode === "plan" ? (slim ? 0 : GROUP_LABEL + SHIFT_LABEL) + rows * ROW + GROUP_PAD : height,
			cards: placed.filter((p) => p.item.card).map((p) => ({ card: p.item.card as LaneCard, row: p.row })),
			slots: placed.filter((p) => p.item.slot).map((p) => ({ slot: p.item.slot as LaneSlot, row: p.row })),
		};
	});
}

/** The fullest form of a name that fits a chip this wide: full name, "First L.", then initials. */
const fitName = (name: string, width: number): string => {
	const room = width - 14;
	const fits = (text: string): boolean => text.length * 6.6 <= room;
	const words = name.split(/\s+/).filter(Boolean);
	if (fits(name)) return name;
	const short = words.length > 1 ? `${words[0]} ${words[words.length - 1][0]}.` : name;
	if (fits(short)) return short;
	if (fits(words[0] ?? name)) return words[0] ?? name;
	return personInitials(name);
};

// ---------------------------------------------------------------------------

/**
 * Location cluster: the plant a person comes from when it is not this one
 * (their shift's plant), e.g. "3725" on the 3701 roster. Undefined for the
 * plant's own people.
 */
const PlantContext = React.createContext<(shiftId?: string | null) => string | undefined>(() => undefined);

const Chip: React.FC<{
	card: LaneCard;
	left: number;
	top: number;
	width: number;
	label: string;
	selected: boolean;
	moves: ManualMoves;
	onSelect: (id: number | null) => void;
	pinnedUntil: string | null;
	ghost: boolean;
	/** Someone else is picked up: a click here hands them to this crew line instead of picking this person. */
	picking: boolean;
	/** Full cards: the ⋯ menu (move from a time, keep for the week, replace, remove) when the chip is wide enough. */
	menu: boolean;
	/** Show the name (as much as fits) instead of initials: focused or hovered card, Day view. */
	fullName: boolean;
}> = ({ card, left, top, width, label, selected, moves, onSelect, pinnedUntil, ghost, picking, menu, fullName }) => {
	const { hover, handlers } = useCardHover();
	const handleUnpin = useCallback(() => void moves.unpin(card.id), [moves, card.id]);
	const handleKeepWeek = useCallback(() => void moves.extend(card.id, "week"), [moves, card.id]);
	const handleKeepUntil = useCallback(() => moves.openUntil(card.id), [moves, card.id]);
	const handleReplace = useCallback(() => moves.replace(card.id), [moves, card.id]);
	const handleRemove = useCallback(() => moves.openRemove(card.id), [moves, card.id]);
	const handleSplit = useCallback(() => void moves.openSplit(card.id), [moves, card.id]);
	const item = useMemo(
		() => ({
			id: card.id,
			label,
			workerId: card.workerId,
			workerName: card.workerName,
			workOrderId: card.workOrderId,
			phaseId: card.phaseId,
			dayIso: card.dayIso,
		}),
		[card.id, label, card.workerId, card.workerName, card.workOrderId, card.phaseId, card.dayIso]
	);
	const { isDragging, dragRef } = useCardDrag(item, moves, !ghost);
	const toggle = useCallback(
		(event: React.MouseEvent) => {
			// While another person is picked up, let the click reach the crew line (the drop target).
			if (picking && !selected) return;
			event.stopPropagation();
			if (!ghost) onSelect(selected ? null : card.id);
		},
		[ghost, onSelect, selected, card.id, picking]
	);
	const onKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (!ghost && (event.key === "Enter" || event.key === " ")) {
				event.preventDefault();
				onSelect(selected ? null : card.id);
			}
		},
		[ghost, onSelect, selected, card.id]
	);
	const fromPlant = useContext(PlantContext)(card.shiftId);
	const pinText = card.isManuallyAssigned ? (pinnedUntil ? ` · ${pinnedUntilLabel(pinnedUntil)}` : " · pinned") : "";
	const plantText = fromPlant ? ` · from ${fromPlant}` : "";
	const title = ghost
		? `${label} · ${hhmm(card.start % 24)} – ${hhmm(card.end % 24)}`
		: `${card.workerName}${plantText} · ${label} · ${clockOf(card.start)} – ${clockOf(card.end)}${pinText}${availabilityTitle(card.unavailable)}`;
	const tiny = width < 26;
	const base: React.CSSProperties = {
		position: "absolute",
		left,
		top,
		width: Math.max(width, 6),
		height: 20,
		boxSizing: "border-box",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: 999,
		fontFamily: lbb.fontUi,
		fontSize: 12,
		fontWeight: 600,
		letterSpacing: "0.04em",
		whiteSpace: "nowrap",
		overflow: tiny ? "visible" : "hidden",
		userSelect: "none",
		zIndex: 1,
	};
	if (ghost) {
		return <span title={title} style={{ ...base, border: `1px dashed ${lbb.navy40}`, background: "transparent" }} />;
	}
	return (
		<span
			ref={dragRef}
			role="button"
			tabIndex={0}
			aria-label={title}
			title={title}
			onClick={toggle}
			onKeyDown={onKeyDown}
			{...handlers}
			style={{
				...base,
				cursor: "grab",
				opacity: isDragging ? 0.35 : 1,
				background: selected ? lbb.brick : tiny ? lbb.navy : card.isManuallyAssigned ? lbb.paper : lbb.blockFill,
				border: selected ? lbb.borderInk : `1px solid ${card.isManuallyAssigned || tiny ? lbb.navy : lbb.navy20}`,
				color: selected ? lbb.paper : lbb.navy,
				...availabilityStripe(card.unavailable),
				// A sister plant's person (location cluster) wears a dashed outline.
				...(fromPlant && !selected ? { borderStyle: "dashed", borderColor: lbb.navy } : {}),
				...(card.isOut && !selected ? { border: `1.5px solid ${lbb.brick}`, background: lbb.brickTint } : {}),
			}}
		>
			<span
				style={
					tiny
						? { position: "absolute", left: "calc(100% + 3px)", fontSize: 10.5, color: lbb.navy, opacity: card.isOut ? OUT_NAME_OPACITY : 1 }
						: { opacity: card.isOut ? OUT_NAME_OPACITY : 1, overflow: "hidden", textOverflow: "clip" }
				}
			>
				{fullName && !tiny
					? fitName(fromPlant ? `${card.workerName} · ${fromPlant}` : card.workerName, width - (menu && width >= 56 ? 14 : 0))
					: personInitials(card.workerName)}
			</span>
			{menu && !tiny && width >= 56 && (
				<span style={{ position: "absolute", right: 3, top: 1 }}>
					<CardMenu
						pinned={card.isManuallyAssigned}
						visible={hover}
						urgent={Boolean(card.unavailable)}
						onUnpin={handleUnpin}
						onKeepWeek={handleKeepWeek}
						onKeepUntil={handleKeepUntil}
						onReplace={handleReplace}
						onRemove={handleRemove}
						onSplit={handleSplit}
						pinnedUntil={pinnedUntil}
						color={selected ? lbb.paper : card.unavailable || card.isManuallyAssigned ? lbb.brick : lbb.ink60}
						size={12}
					/>
				</span>
			)}
		</span>
	);
};

const Slot: React.FC<{
	slot: LaneSlot;
	left: number;
	top: number;
	width: number;
	jobRole: string;
	ghost: boolean;
	onAssign: (slot: LaneSlot) => void;
	picking: boolean;
}> = ({ slot, left, top, width, jobRole, ghost, onAssign, picking }) => {
	const handleClick = useCallback(
		(event: React.MouseEvent) => {
			if (picking) return;
			event.stopPropagation();
			if (!ghost) onAssign(slot);
		},
		[ghost, onAssign, slot, picking]
	);
	return (
		<button
			type="button"
			onClick={handleClick}
			title={`${ghost ? "" : "Assign "}${jobRole} · ${clockOf(slot.start)} – ${clockOf(slot.end)}`}
			aria-label={`Assign ${jobRole} ${clockOf(slot.start)} to ${clockOf(slot.end)}`}
			style={{
				position: "absolute",
				left,
				top,
				width: Math.max(width, 6),
				height: 20,
				boxSizing: "border-box",
				borderRadius: 999,
				border: ghost ? `1px dashed ${lbb.navy40}` : `1px dashed ${lbb.brick}`,
				background: ghost ? "transparent" : lbb.brickTint,
				color: lbb.brick,
				fontFamily: lbb.fontUi,
				fontSize: 11,
				fontWeight: 700,
				padding: 0,
				cursor: ghost ? "default" : "pointer",
				zIndex: 1,
			}}
		>
			{ghost || width < 14 ? "" : "+"}
		</button>
	);
};

// ---------------------------------------------------------------------------

/** One crew line of a step: the drop target, its shift labels and its positioned chips and slots. */
const Group: React.FC<{
	step: TimelineStep;
	layout: GroupLayout;
	axis: Axis;
	cardLeft: number;
	slim: boolean;
	narrow: boolean;
	/** The card's width: very narrow cards drop the crew count so the code fits. */
	cardWidth: number;
	/** Chips show names rather than initials. */
	fullNames: boolean;
	mode: Mode;
	weekStart: Date;
	shifts: ShiftInstance[];
	selected: number | null;
	moves: ManualMoves;
	onSelect: (id: number | null) => void;
	onFill: (request: FillRequest) => void;
	pinEndFor: (workerId: string, day: string) => string | null;
	workstation: string;
}> = ({ step, layout, axis, cardLeft, slim, narrow, cardWidth, fullNames, mode, weekStart, shifts, selected, moves, onSelect, onFill, pinEndFor, workstation }) => {
	const { group } = layout;
	const droppable = group.dropPhaseId !== null && mode === "roster";
	const target = useMemo<MoveTarget>(
		() => ({ kind: "phase", workOrderId: step.workOrderId, phaseId: group.dropPhaseId ?? -1, label: `${step.title} · ${group.operationName}` }),
		[step.workOrderId, step.title, group.dropPhaseId, group.operationName]
	);
	const { targetIndex } = moves;
	const grade = useMemo(
		() => (droppable ? bestPhaseTarget(group.phaseIds.map((id) => targetIndex.phase(step.workOrderId, id))) : undefined),
		[droppable, group.phaseIds, targetIndex, step.workOrderId]
	);
	const state = useMemo<DropState>(
		() => (droppable ? phaseDropState(grade, targetIndex.loaded) : { ...phaseDropState(undefined), allowed: false }),
		[droppable, grade, targetIndex.loaded]
	);
	const handlePlace = useCallback(
		(worker: WorkerDragItem) => {
			const open = group.slots[0];
			const start = open ? open.startIso : isoAt(weekStart, group.start);
			const end = open ? open.endIso : isoAt(weekStart, group.end);
			void moves.placeWorker({
				workerId: worker.workerId,
				workerName: worker.workerName,
				workOrderId: step.workOrderId,
				phaseId: open?.phaseId ?? group.dropPhaseId,
				jobRoleId: group.jobRoleId,
				jobRoleName: group.jobRole,
				start,
				end,
				workstation,
				stepTitle: step.title,
			});
		},
		[group, weekStart, moves, step.workOrderId, step.title, workstation]
	);
	// A timeline card is one block: a drop asks "whole block or for a while".
	const { isOver, canDrop, chipOver, dropRef } = useCardDrop(target, state, moves, false, droppable ? handlePlace : undefined);
	const receiving = selected !== null && droppable;
	const handoff = useCallback(
		(event: React.MouseEvent) => {
			if (selected !== null && droppable && state.allowed) {
				event.stopPropagation();
				moves.requestMove(selected, target, {
					wholeStep: false,
					confirmFirst: state.confirm ? `${state.caption || "Not qualified"} for ${group.jobRole}. Place anyway?` : null,
				});
				onSelect(null);
			}
		},
		[selected, droppable, state.allowed, state.confirm, state.caption, moves, target, group.jobRole, onSelect]
	);
	const handleAssign = useCallback(
		(slot: LaneSlot) =>
			onFill({
				workOrderId: step.workOrderId,
				phaseId: slot.phaseId,
				start: slot.startIso,
				end: slot.endIso,
				jobRoleId: group.jobRoleId,
				jobRoleName: group.jobRole,
				title: `${step.title} · ${group.operationName} · ${group.jobRole}`,
			}),
		[onFill, step.workOrderId, step.title, group.jobRoleId, group.jobRole, group.operationName]
	);
	// One shift label per shift, where that shift's first person starts.
	const shiftMarks = useMemo(() => {
		const first = new Map<string, number>();
		group.cards.forEach((card) => {
			const label = shiftLabelFor(card.shiftId, card.start % 24, shifts);
			first.set(label, Math.min(first.get(label) ?? Infinity, card.start));
		});
		return Array.from(first.entries()).sort((a, b) => a[1] - b[1]);
	}, [group.cards, shifts]);
	const dragging = Boolean(moves.dragging);
	const top0 = slim ? 0 : GROUP_LABEL + SHIFT_LABEL;
	const name = narrow ? roleCode(group.operationName) : group.operationName;

	return (
		<div
			ref={dropRef}
			role={receiving ? "button" : undefined}
			aria-label={receiving ? `Move the selected block to ${step.title} · ${group.operationName}` : undefined}
			onClick={handoff}
			style={{
				position: "relative",
				height: layout.height,
				borderTop: slim ? `1px dashed ${lbb.hairline}` : lbb.borderHair,
				cursor: receiving ? "copy" : "inherit",
				background: receiving || chipOver ? lbb.dropTint : "transparent",
				...dropSurface(state, (dragging || receiving) && droppable, isOver, canDrop),
				...(chipOver ? { outline: `2px solid ${lbb.navy}`, outlineOffset: -2 } : {}),
			}}
		>
			{!slim && (
				<div style={{ display: "flex", alignItems: "baseline", gap: 5, height: GROUP_LABEL, padding: "3px 8px 0", minWidth: 0 }}>
					<span
						title={group.jobRole ? `${group.operationName} · ${group.jobRole}` : group.operationName}
						style={{ ...eyebrowSx, fontSize: narrow ? 10.5 : 9.5, letterSpacing: narrow ? "0.02em" : eyebrowSx.letterSpacing, textTransform: narrow ? "none" : "uppercase", color: lbb.ink60, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
					>
						{name}
					</span>
					{group.needed > 0 && cardWidth >= 90 && (
						<span style={{ fontFamily: lbb.fontMono, fontSize: 10, fontWeight: 700, color: mode === "plan" ? lbb.ink60 : crewColor(group.have, group.needed) }}>
							{mode === "plan" ? `–/${group.needed}` : `${group.have}/${group.needed}`}
						</span>
					)}
					{(dragging || receiving) && droppable && state.caption && (
						<span style={{ fontFamily: lbb.fontUi, fontSize: 10, color: state.outline, marginLeft: "auto", whiteSpace: "nowrap" }}>{state.caption}</span>
					)}
				</div>
			)}
			{!slim &&
				shiftMarks.map(([label, start]) => (
					<span
						key={label}
						style={{ position: "absolute", top: GROUP_LABEL + 1, left: Math.max(2, axis.x(start) - cardLeft + 2), fontFamily: lbb.fontMono, fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase", color: lbb.navy40, whiteSpace: "nowrap" }}
					>
						<span style={{ color: isDayShift(label) ? lbb.warning : lbb.ink60, marginRight: 3 }}>{isDayShift(label) ? "☀" : "☾"}</span>
						{label}
					</span>
				))}
			{layout.cards.map(({ card, row }) => (
				<Chip
					key={card.id}
					card={card}
					left={axis.x(card.start) - cardLeft}
					top={top0 + row * ROW + 2}
					width={axis.w(card.start, card.end) - 2}
					label={group.operationName}
					selected={selected === card.id}
					moves={moves}
					onSelect={onSelect}
					pinnedUntil={pinEndFor(card.workerId, card.dayIso)}
					ghost={mode === "plan"}
					picking={selected !== null}
					menu
					fullName={fullNames}
				/>
			))}
			{layout.slots.map(({ slot, row }) => (
				<Slot
					key={`${slot.phaseId}-${slot.startIso}`}
					slot={slot}
					left={axis.x(slot.start) - cardLeft}
					top={top0 + row * ROW + 2}
					width={axis.w(slot.start, slot.end) - 2}
					jobRole={group.jobRole}
					ghost={mode === "plan"}
					onAssign={handleAssign}
					picking={selected !== null}
				/>
			))}
		</div>
	);
};

// ---------------------------------------------------------------------------

const Card: React.FC<{
	step: TimelineStep;
	axis: Axis;
	height: number;
	slim: boolean;
	focused: boolean;
	mode: Mode;
	weekStart: Date;
	shifts: ShiftInstance[];
	selected: number | null;
	moves: ManualMoves;
	onSelect: (id: number | null) => void;
	onFill: (request: FillRequest) => void;
	onFocus: (step: TimelineStep | null) => void;
	pinEndFor: (workerId: string, day: string) => string | null;
	workstation: string;
	layouts: GroupLayout[];
	spansDays: boolean;
}> = ({ step, axis, height, slim, focused, mode, weekStart, shifts, selected, moves, onSelect, onFill, onFocus, pinEndFor, workstation, layouts, spansDays }) => {
	const left = axis.x(step.start) + 2;
	const width = Math.max(axis.w(step.start, step.end) - 4, 8);
	const [hovered, setHovered] = useState(false);
	const enter = useCallback(() => setHovered(true), []);
	const leave = useCallback(() => setHovered(false), []);
	// Names instead of initials when the card is open (focus, Day view) or under the pointer.
	const fullNames = !slim || focused || hovered;
	const narrow = width < NARROW;
	const isActivity = /^(hold|swab|type-a clean|changeover|co|cleaning)$/i.test(step.title.trim());
	const toggleFocus = useCallback(
		(event: React.MouseEvent) => {
			if (selected !== null) return;
			event.stopPropagation();
			onFocus(focused ? null : step);
		},
		[selected, onFocus, focused, step]
	);
	const fullTitle = `${step.title}${step.subtitle ? ` · ${step.subtitle}` : ""} · ${format(addDays(weekStart, Math.floor(step.start / 24)), "EEE")} ${clockOf(step.start)} → ${format(addDays(weekStart, Math.floor((step.end - 0.001) / 24)), "EEE")} ${clockOf(step.end)} · ${step.crewHave} of ${step.crewNeeded} crew`;
	return (
		<div
			onMouseEnter={enter}
			onMouseLeave={leave}
			onClick={slim ? toggleFocus : undefined}
			title={slim ? `${fullTitle} · click to focus` : undefined}
			style={{
				position: "absolute",
				top: 8,
				left,
				width,
				height,
				boxSizing: "border-box",
				border: `1px solid ${lbb.navy20}`,
				borderRadius: 4,
				background: lbb.paper,
				overflow: "hidden",
				cursor: slim ? "zoom-in" : "default",
				zIndex: focused ? 5 : spansDays ? 4 : 2,
				boxShadow: focused ? "0 8px 22px rgba(18,24,38,.18)" : spansDays ? "0 1px 4px rgba(18,24,38,.12)" : "none",
				outline: focused ? `2px solid ${lbb.brick}` : "none",
				outlineOffset: 1,
			}}
		>
			{slim ? (
				<div
					style={{
						height: HEADER_SLIM,
						boxSizing: "border-box",
						padding: "0 6px",
						display: "flex",
						alignItems: "center",
						gap: 5,
						overflow: "hidden",
						whiteSpace: "nowrap",
						fontFamily: lbb.fontMono,
						fontSize: 10,
						fontWeight: 700,
						letterSpacing: "0.02em",
						color: isActivity ? lbb.ink : lbb.paper,
						background: isActivity ? `repeating-linear-gradient(135deg, ${lbb.alloy} 0 5px, ${lbb.alloyWarm} 5px 10px)` : lbb.navy,
						borderBottom: isActivity ? `1px solid ${lbb.navy20}` : "none",
					}}
				>
					<span style={{ flexShrink: 0 }}>{productCode(step.title)}</span>
					{step.subtitle && width > 90 && <span style={{ fontWeight: 400, opacity: 0.8, overflow: "hidden", textOverflow: "ellipsis" }}>{step.subtitle}</span>}
					{step.crewOut > 0 && (
						<span
							title={`${step.crewOut} assigned ${step.crewOut === 1 ? "person is" : "people are"} out — hover their chip and use ⋯ to replace`}
							style={{ marginLeft: "auto", flexShrink: 0, padding: "0 5px", borderRadius: 999, background: lbb.brick, color: lbb.paper, fontSize: 9.5, lineHeight: "13px" }}
						>
							{step.crewOut} out
						</span>
					)}
				</div>
			) : (
				<button
					type="button"
					onClick={toggleFocus}
					title={`${fullTitle} · click to ${focused ? "show the whole week" : "focus"}`}
					style={{
						display: "flex",
						alignItems: "baseline",
						gap: 6,
						width: "100%",
						height: HEADER_FULL,
						padding: "6px 8px",
						border: "none",
						background: isActivity ? `repeating-linear-gradient(135deg, ${lbb.alloy} 0 6px, ${lbb.alloyWarm} 6px 12px)` : lbb.alloyWarm,
						cursor: focused ? "zoom-out" : "zoom-in",
						font: "inherit",
						textAlign: "left",
						whiteSpace: "nowrap",
						minWidth: 0,
					}}
				>
					<span style={{ fontFamily: narrow ? lbb.fontMono : lbb.fontUi, fontSize: 12.5, fontWeight: 700, color: lbb.ink, overflow: narrow ? "visible" : "hidden", textOverflow: "ellipsis", flexShrink: narrow ? 0 : 1, minWidth: 0 }}>
						{narrow ? productCode(step.title) : step.title}
					</span>
					{step.subtitle && width > 200 && <span style={{ fontFamily: lbb.fontMono, fontSize: 10.5, color: lbb.ink60, flexShrink: 0 }}>{step.subtitle}</span>}
					{width > 380 && (
						<span style={{ fontFamily: lbb.fontMono, fontSize: 10.5, color: lbb.ink60, flexShrink: 0 }}>
							{format(addDays(weekStart, Math.floor(step.start / 24)), "EEE")} {clockOf(step.start)} → {format(addDays(weekStart, Math.floor((step.end - 0.001) / 24)), "EEE")} {clockOf(step.end)}
						</span>
					)}
					<span style={{ marginLeft: "auto", fontFamily: lbb.fontMono, fontSize: 12, fontWeight: 700, color: mode === "plan" ? lbb.ink60 : crewColor(step.crewHave, step.crewNeeded), minWidth: 0, overflow: "hidden" }}>
						{mode === "plan" ? `–/${step.crewNeeded}` : `${step.crewHave}/${step.crewNeeded}`}
						{mode === "roster" && <OutCount count={step.crewOut} size={11} />}
					</span>
				</button>
			)}
			{/* The night shade carries through the card, behind its crew rows. */}
			{Array.from({ length: 9 }, (_, k) => [k * 24 - 5, k * 24 + 7] as const)
				.filter(([a, b]) => a < step.end && b > step.start)
				.map(([a, b]) => {
					const from = Math.max(a, step.start);
					const to = Math.min(b, step.end);
					return <div key={a} style={{ position: "absolute", top: 0, bottom: 0, left: axis.x(from) - left, width: axis.w(from, to), background: NIGHT, pointerEvents: "none" }} />;
				})}
			{step.phases.length === 0 && !slim && (
				<div style={{ padding: "4px 8px", fontFamily: lbb.fontUi, fontSize: 11, color: lbb.ink60 }}>No labor standard matched.</div>
			)}
			{layouts.map((layout) => (
				<Group
					key={layout.group.key}
					step={step}
					layout={layout}
					axis={axis}
					cardLeft={left}
					slim={slim}
					narrow={narrow}
					cardWidth={width}
					fullNames={fullNames}
					mode={mode}
					weekStart={weekStart}
					shifts={shifts}
					selected={selected}
					moves={moves}
					onSelect={onSelect}
					onFill={onFill}
					pinEndFor={pinEndFor}
					workstation={workstation}
				/>
			))}
		</div>
	);
};

// ---------------------------------------------------------------------------

const Lane: React.FC<{
	lane: TimelineLane;
	axis: Axis;
	weekStart: Date;
	mode: Mode;
	slimAll: boolean;
	focusId: number | null;
	shifts: ShiftInstance[];
	selected: number | null;
	moves: ManualMoves;
	onSelect: (id: number | null) => void;
	onFill: (request: FillRequest) => void;
	onFocus: (step: TimelineStep | null) => void;
	pinEnd: PinEndOnDay;
	hourStep: number;
}> = ({ lane, axis, weekStart, mode, slimAll, focusId, shifts, selected, moves, onSelect, onFill, onFocus, pinEnd, hourStep }) => {
	const { isOver, dragging, dropRef } = useLaneHeaderDrop(lane.workcenter, moves);
	const pinEndFor = useCallback((workerId: string, day: string) => pinEnd(workerId, lane.workcenter, day), [pinEnd, lane.workcenter]);
	const drafts = moves.draftPins.filter((d) => d.workstation === lane.workcenter);
	const cards = useMemo(
		() =>
			lane.steps.map((step) => {
				const slim = slimAll && focusId !== step.workOrderId;
				const layouts = layoutGroups(step, slim, mode);
				const height = (slim ? HEADER_SLIM : HEADER_FULL) + layouts.reduce((t, l) => t + l.height, 0) + (step.phases.length === 0 && !slim ? 22 : 0);
				const spansDays = Math.floor((step.start - axis.dayStart) / 24) !== Math.floor((step.end - axis.dayStart - 0.001) / 24);
				return { step, slim, layouts, height, spansDays };
			}),
		[lane.steps, slimAll, focusId, mode, axis.dayStart]
	);
	// Every card on a line is as tall as the tallest one.
	const cardHeight = Math.max(24, ...cards.map((c) => c.height));
	const laneHeight = lane.idle ? 34 : cardHeight + 16;
	return (
		<div style={{ display: "grid", gridTemplateColumns: `${LABEL_WIDTH}px ${axis.width}px`, borderBottom: lbb.borderHair }}>
			<div
				ref={dropRef}
				title={`Drop a name here to keep them on ${lane.workcenter} for the week`}
				style={{
					position: "sticky",
					left: 0,
					zIndex: 6,
					background: lbb.paper,
					padding: "10px 12px",
					borderRight: lbb.borderHair,
					display: "flex",
					flexDirection: "column",
					gap: 4,
					...(dragging ? { outline: `2px ${isOver ? "solid" : "dashed"} ${lbb.navy}`, outlineOffset: -2 } : {}),
				}}
			>
				<span style={{ fontFamily: lbb.fontUi, fontSize: lane.idle ? 12 : 14, fontWeight: 600, color: lane.idle ? lbb.ink60 : lbb.navy }}>{lane.workcenter}</span>
				<span style={{ fontFamily: lbb.fontMono, fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: lbb.ink60 }}>
					{lane.idle ? "idle this week" : `${Math.round(lane.weekHours)}h this week`}
				</span>
				{drafts.length > 0 && <span style={{ fontFamily: lbb.fontUi, fontSize: 11, color: lbb.brick }}>{drafts.length} drafted</span>}
			</div>
			<div style={{ position: "relative", height: laneHeight }}>
				<Backdrop axis={axis} step={hourStep} />
				{cards.map(({ step, slim, layouts, spansDays }) => (
					<Card
						key={step.workOrderId}
						step={step}
						axis={axis}
						height={cardHeight}
						slim={slim}
						focused={focusId === step.workOrderId}
						mode={mode}
						weekStart={weekStart}
						shifts={shifts}
						selected={selected}
						moves={moves}
						onSelect={onSelect}
						onFill={onFill}
						onFocus={onFocus}
						pinEndFor={pinEndFor}
						workstation={lane.workcenter}
						layouts={layouts}
						spansDays={spansDays}
					/>
				))}
				<DayLines axis={axis} />
			</div>
		</div>
	);
};

/** Night shading and 4-hour grid lines behind a lane. */
const Backdrop: React.FC<{ axis: Axis; step: number }> = React.memo(({ axis, step }) => (
	<>
		{Array.from({ length: 9 }, (_, k) => (
			<div key={`n${k}`} style={{ position: "absolute", top: 0, bottom: 0, left: axis.x(k * 24 - 5), width: axis.w(k * 24 - 5, k * 24 + 7), background: NIGHT, pointerEvents: "none" }} />
		))}
		{Array.from({ length: Math.ceil(168 / step) + 1 }, (_, i) => Math.ceil(axis.dayStart / step) * step + i * step)
			.filter((t) => Math.abs((t - axis.dayStart) % 24) > 0.01 && t < 168 + axis.dayStart)
			.map((t) => (
				<div key={`h${t}`} style={{ position: "absolute", top: 0, bottom: 0, left: axis.x(t), borderLeft: "1px solid #F0F1F4", pointerEvents: "none" }} />
			))}
	</>
));
Backdrop.displayName = "Backdrop";

/** The line where one production day ends and the next starts, over the cards. */
const DayLines: React.FC<{ axis: Axis }> = React.memo(({ axis }) => (
	<>
		{Array.from({ length: 6 }, (_, i) => (
			<div key={i} style={{ position: "absolute", top: 0, bottom: 0, left: axis.x((i + 1) * 24 + axis.dayStart), borderLeft: `2px solid ${lbb.ink}`, opacity: 0.55, pointerEvents: "none", zIndex: 3 }} />
		))}
	</>
));
DayLines.displayName = "DayLines";

// ---------------------------------------------------------------------------

/**
 * The People column doubles as the way off the roster: drop a chip on it — or
 * pick a chip, then click here — to unassign that block (with the usual confirm).
 */
const PeopleColumn: React.FC<{ workers: KnownWorker[]; draftedOn: Map<string, string>; moves: ManualMoves; selected: number | null; onSelect: (id: number | null) => void }> = ({
	workers,
	draftedOn,
	moves,
	selected,
	onSelect,
}) => {
	const { openRemove } = moves;
	const [{ isOver, carrying }, dropRef] = useDrop(
		() => ({
			accept: CARD_TYPE,
			drop: (item: CardDragItem) => openRemove(item.id),
			collect: (monitor) => ({ isOver: monitor.isOver(), carrying: monitor.getItemType() === CARD_TYPE }),
		}),
		[openRemove]
	);
	const picking = selected !== null;
	const handleClick = useCallback(() => {
		if (selected !== null) {
			openRemove(selected);
			onSelect(null);
		}
	}, [selected, openRemove, onSelect]);
	const active = carrying || picking;
	return (
		<div
			ref={dropRef}
			onClickCapture={picking ? handleClick : undefined}
			style={{ position: "relative", alignSelf: "stretch", cursor: picking ? "pointer" : "default" }}
			title={active ? "Drop here to take this person off the step" : undefined}
		>
			<PeopleList title="People" hint="Drop a name on a crew line to place them; on a line name to keep them there all week. Drag a chip back here to unassign it." workers={workers} draftedOn={draftedOn} emptyText="Nobody is rostered this week." />
			{active && (
				<div
					style={{
						position: "absolute",
						inset: 4,
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "center",
						paddingTop: 48,
						borderRadius: 6,
						border: `2px dashed ${lbb.brick}`,
						background: isOver ? "rgba(200, 94, 81, 0.16)" : "rgba(251, 239, 236, 0.85)",
						color: lbb.brick,
						fontFamily: lbb.fontUi,
						fontSize: 13,
						fontWeight: 700,
						textAlign: "center",
						pointerEvents: carrying ? "auto" : "none",
						zIndex: 8,
					}}
				>
					{carrying ? "Drop to unassign" : "Click here to unassign the picked person"}
				</div>
			)}
		</div>
	);
};

export interface TimelineViewProps {
	timeline: Timeline;
	weekStart: Date;
	workers: KnownWorker[];
	shifts: ShiftInstance[];
	selected: number | null;
	onSelect: (id: number | null) => void;
	moves: ManualMoves;
	onFill: (request: FillRequest) => void;
	pinEnd: PinEndOnDay;
	/** Open a day of the week in the Day view (by line). */
	onOpenDay?: (date: Date) => void;
	/** Go back to the classic day-column week board. */
	onClassic?: () => void;
	/** Week: the whole week, cards show their people only. Day: one day across the screen, full cards; scroll on into the next days. */
	zoom: "week" | "day";
	/** The day to bring into view (Day zoom). */
	focusDate?: Date;
	/** This plant; with plantNames, people on another plant's shift (location cluster) are labelled with it. */
	locationId?: number;
	plantNames?: Record<number, string>;
}

const TimelineView: React.FC<TimelineViewProps> = ({ timeline, weekStart, workers, shifts, selected, onSelect, moves, onFill, pinEnd, onOpenDay, onClassic, zoom, focusDate, locationId, plantNames }) => {
	const fromPlant = useMemo(() => {
		const plantOfShift = new Map(shifts.map((shift) => [shift.shiftId, shift.locationId]));
		return (shiftId?: string | null) => {
			const plant = shiftId ? plantOfShift.get(shiftId) : undefined;
			return plant && locationId && plant !== locationId ? plantNames?.[plant] ?? String(plant) : undefined;
		};
	}, [shifts, locationId, plantNames]);
	const mode: Mode = "roster";
	const [dayStart, setDayStart] = useState<number>(() => (Number(readSetting("roster.timeline.dayStart", "7")) === 0 ? 0 : 7));
	const [hideIdle, setHideIdle] = useState<boolean>(() => readSetting("roster.timeline.hideIdle", "0") === "1");
	const [focusStep, setFocusStep] = useState<TimelineStep | null>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const [viewport, setViewport] = useState(1200);

	useEffect(() => {
		const node = scrollRef.current;
		if (!node || typeof ResizeObserver === "undefined") return undefined;
		const observer = new ResizeObserver((entries) => setViewport(entries[0].contentRect.width));
		observer.observe(node);
		return () => observer.disconnect();
	}, []);
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape" && focusStep && selected === null) setFocusStep(null);
			// Delete / Backspace on a picked chip: unassign it (asks first).
			const typing = event.target instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName);
			if ((event.key === "Delete" || event.key === "Backspace") && selected !== null && !typing) {
				event.preventDefault();
				moves.openRemove(selected);
				onSelect(null);
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [focusStep, selected, moves, onSelect]);

	const changeDayStart = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
		setDayStart(Number(event.target.value));
		writeSetting("roster.timeline.dayStart", event.target.value);
	}, []);
	const changeIdle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setHideIdle(event.target.checked);
		writeSetting("roster.timeline.hideIdle", event.target.checked ? "1" : "0");
	}, []);
	const clearFocus = useCallback(() => setFocusStep(null), []);
	const openFocusDay = useCallback(() => {
		if (focusStep && onOpenDay) onOpenDay(addDays(weekStart, Math.max(0, Math.floor(focusStep.start / 24))));
	}, [focusStep, onOpenDay, weekStart]);

	const busy = useMemo(() => timeline.lanes.flatMap((lane) => lane.steps.map((step) => ({ start: step.start, end: step.end }))), [timeline]);
	const dayWidth = zoom === "day" ? Math.max(720, viewport - LABEL_WIDTH) : WEEK_DAY_WIDTH;
	const hourStep = dayWidth >= 600 ? 1 : 4;
	const axis = useMemo(
		() => buildAxis({ dayWidth, dayStart, hideIdle, busy, focus: focusStep ? { start: focusStep.start, end: focusStep.end } : null }),
		[dayWidth, dayStart, hideIdle, busy, focusStep]
	);
	// Keep the focused card in view.
	useEffect(() => {
		const node = scrollRef.current;
		if (node && focusStep) {
			const mid = (axis.x(focusStep.start) + axis.x(focusStep.end)) / 2;
			node.scrollLeft = Math.max(0, mid - (node.clientWidth - LABEL_WIDTH) / 2);
		}
	}, [focusStep, axis]);
	// Day zoom: bring the chosen day to the left edge; the next days follow on scroll.
	const focusDay = focusDate ? Math.min(6, Math.max(0, differenceInCalendarDays(focusDate, weekStart))) : 0;
	useEffect(() => {
		const node = scrollRef.current;
		if (node && zoom === "day" && !focusStep) node.scrollLeft = axis.x(focusDay * 24 + axis.dayStart);
	}, [zoom, focusDay, axis, focusStep]);

	const sortedWorkers = useMemo(() => [...workers].sort((a, b) => a.name.localeCompare(b.name)), [workers]);
	const draftedOn = useMemo(() => new Map(moves.draftPins.map((d) => [String(d.workerId), d.workstation])), [moves.draftPins]);
	const busyLanes = timeline.lanes.filter((lane) => !lane.idle);
	const idleLanes = timeline.lanes.filter((lane) => lane.idle);
	const slimAll = zoom === "week";

	return (
		<PlantContext.Provider value={fromPlant}>
		<div style={{ display: "grid", gridTemplateColumns: `${WORKER_LIST_WIDTH}px minmax(0, 1fr)`, alignItems: "start" }}>
			<PeopleColumn workers={sortedWorkers} draftedOn={draftedOn} moves={moves} selected={selected} onSelect={onSelect} />
			<div style={{ minWidth: 0 }}>
				<div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 16px", borderBottom: lbb.borderHair, flexWrap: "wrap" }}>
					<label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: lbb.fontUi, fontSize: 12, fontWeight: 600, color: lbb.navy }}>
						Day starts
						<select value={String(dayStart)} onChange={changeDayStart} style={{ font: "inherit", fontFamily: lbb.fontMono, padding: "2px 6px", border: lbb.borderInk, borderRadius: 4, background: lbb.paper }}>
							<option value="7">7 am</option>
							<option value="0">12 am</option>
						</select>
					</label>
					<label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: lbb.fontUi, fontSize: 12, fontWeight: 600, color: lbb.navy, cursor: "pointer" }}>
						<input type="checkbox" checked={hideIdle} onChange={changeIdle} /> Hide idle hours
					</label>
					{focusStep && (
						<span style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "3px 6px 3px 10px", borderRadius: 999, background: lbb.brickTint, border: `1px solid ${lbb.brick}`, color: lbb.brick, fontFamily: lbb.fontUi, fontSize: 12, fontWeight: 700 }}>
							Focus: {focusStep.subtitle || focusStep.title}
							{onOpenDay && (
								<button type="button" onClick={openFocusDay} style={{ border: "none", background: "none", color: lbb.brick, textDecoration: "underline", cursor: "pointer", font: "inherit", fontWeight: 600, padding: 0 }}>
									Open day
								</button>
							)}
							<button type="button" onClick={clearFocus} aria-label="Show the whole week evenly" style={{ border: "none", background: lbb.brick, color: lbb.paper, borderRadius: 999, width: 20, height: 20, cursor: "pointer", lineHeight: 1 }}>
								×
							</button>
						</span>
					)}
					<span style={{ marginLeft: "auto", fontFamily: lbb.fontUi, fontSize: 11, color: lbb.ink60 }}>
						{zoom === "week" ? "Click a card to see everything on it · drag a chip to move" : "Scroll sideways for the next days · drag a chip to move"}
					</span>
					{onClassic && (
						<button type="button" onClick={onClassic} style={{ border: "none", background: "none", color: lbb.ink60, textDecoration: "underline", cursor: "pointer", font: "inherit", fontSize: 11, padding: 0 }}>
							Classic board
						</button>
					)}
				</div>

				<div ref={scrollRef} style={{ overflowX: "auto", minWidth: 0 }}>
					<div style={{ width: LABEL_WIDTH + axis.width }}>
						<DaysHeader axis={axis} timeline={timeline} weekStart={weekStart} step={hourStep} />
						{busyLanes.length === 0 && (
							<div style={{ padding: 48, textAlign: "center", fontFamily: lbb.fontUi, color: lbb.ink60 }}>No steps are planned this week.</div>
						)}
						{[...busyLanes, ...idleLanes].map((lane) => (
							<Lane
								key={lane.workcenter}
								lane={lane}
								axis={axis}
								weekStart={weekStart}
								mode={mode}
								slimAll={slimAll}
								focusId={focusStep?.workOrderId ?? null}
								shifts={shifts}
								selected={selected}
								moves={moves}
								onSelect={onSelect}
								onFill={onFill}
								onFocus={setFocusStep}
								pinEnd={pinEnd}
								hourStep={hourStep}
							/>
						))}
					</div>
				</div>
			</div>
		</div>
		</PlantContext.Provider>
	);
};

const DaysHeader: React.FC<{ axis: Axis; timeline: Timeline; weekStart: Date; step: number }> = ({ axis, timeline, weekStart, step }) => {
	const ticks = useMemo(() => {
		const out: number[] = [];
		for (let t = Math.ceil(axis.dayStart / step) * step; t < 168 + axis.dayStart; t += step) out.push(t);
		return out;
	}, [axis.dayStart, step]);
	return (
		<div style={{ display: "grid", gridTemplateColumns: `${LABEL_WIDTH}px ${axis.width}px`, borderBottom: lbb.borderHair }}>
			<div style={{ position: "sticky", left: 0, zIndex: 6, background: lbb.paper, padding: "10px 12px", ...eyebrowSx, color: lbb.ink60, display: "flex", alignItems: "flex-end", borderRight: lbb.borderHair }}>
				Line · week hours
			</div>
			<div style={{ position: "relative", height: 62 }}>
				{Array.from({ length: 9 }, (_, k) => (
					<div key={`n${k}`} style={{ position: "absolute", bottom: 0, height: 18, left: axis.x(k * 24 - 5), width: axis.w(k * 24 - 5, k * 24 + 7), background: "rgba(26, 36, 66, 0.07)" }} />
				))}
				{Array.from({ length: 7 }, (_, d) => {
					const from = d * 24 + axis.dayStart;
					const head = timeline.days[d];
					const pct = head?.coveragePct ?? null;
					return (
						<div key={d} style={{ position: "absolute", top: 0, bottom: 18, left: axis.x(from), width: axis.w(from, from + 24), padding: "8px 10px", boxSizing: "border-box", borderLeft: d === 0 ? "none" : "2px solid rgba(18,24,38,.55)", display: "flex", flexDirection: "column", gap: 4, overflow: "hidden" }}>
							<span style={{ fontFamily: lbb.fontMono, fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: lbb.ink, whiteSpace: "nowrap" }}>
								{DOW[d]} <span style={{ fontWeight: 400, color: lbb.ink60 }}>{format(addDays(weekStart, d), "d")}</span>
							</span>
							<span style={{ fontFamily: lbb.fontMono, fontSize: 11, color: pct === null ? lbb.navy40 : pct >= 90 ? lbb.success : lbb.warning, whiteSpace: "nowrap" }}>
								{pct === null ? "—" : `${pct}% covered`}
								<OutCount count={head?.outCount ?? 0} size={11} />
							</span>
						</div>
					);
				})}
				{ticks.map((t) => {
					const width = axis.w(t, t + step);
					return (
						<span key={t} style={{ position: "absolute", bottom: 0, height: 18, left: axis.x(t), width, borderLeft: `1px solid ${lbb.navy20}`, paddingLeft: 3, boxSizing: "border-box", fontFamily: lbb.fontMono, fontSize: 10, lineHeight: "18px", color: lbb.ink60, overflow: "hidden", whiteSpace: "nowrap" }}>
							{width >= 26 ? hourLabel(t) : ""}
						</span>
					);
				})}
			</div>
		</div>
	);
};

export default TimelineView;
